import { ShieldCheck, AlertTriangle, CheckCircle2, Circle } from "lucide-react";

const STYLES = {
  "Not Ready": { bar: "bg-red-500", text: "text-red-600", box: "border-red-200 bg-red-50" },
  "Underprepared": { bar: "bg-amber-500", text: "text-amber-600", box: "border-amber-200 bg-amber-50" },
  "Nearly Ready": { bar: "bg-yellow-500", text: "text-yellow-700", box: "border-yellow-200 bg-yellow-50" },
  "Competition Ready": { bar: "bg-green-500", text: "text-green-700", box: "border-green-200 bg-green-50" },
  "Highly Prepared": { bar: "bg-emerald-600", text: "text-emerald-700", box: "border-emerald-200 bg-emerald-50" },
};

export const ReadinessPill = ({ readiness }) => {
  if (!readiness) return null;
  const s = STYLES[readiness.level] || STYLES["Not Ready"];
  return (
    <span data-testid="readiness-pill" className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${s.box} ${s.text}`}>
      <ShieldCheck size={12} /> {readiness.level}
    </span>
  );
};

export const ReadinessMeter = ({ readiness, coreSkills }) => {
  if (!readiness) return null;
  const s = STYLES[readiness.level] || STYLES["Not Ready"];
  return (
    <div className={`mt-3 rounded-2xl border p-4 shadow-sm ${s.box}`} data-testid="readiness-meter">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-primary">
          <ShieldCheck size={14} /> Competition Readiness
        </p>
        <span data-testid="readiness-level" className={`text-sm font-extrabold ${s.text}`}>{readiness.level}</span>
      </div>
      <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-white/70">
        <div className={`h-full rounded-full transition-all duration-500 ${s.bar}`} style={{ width: `${readiness.score}%` }} data-testid="readiness-bar" />
      </div>
      <div className="mt-1 flex items-center justify-between text-[11px] font-semibold text-muted-foreground">
        <span>{readiness.score}% ready</span>
        <span data-testid="readiness-core-count">Core skills {readiness.core_done.length}/{readiness.core_total}</span>
      </div>

      {coreSkills && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="readiness-core-list">
          {coreSkills.map((sk) => {
            const done = readiness.core_done.includes(sk);
            return (
              <span key={sk} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${done ? "bg-green-100 text-green-700" : "bg-white/70 text-muted-foreground"}`}>
                {done ? <CheckCircle2 size={10} /> : <Circle size={10} />} {sk}
              </span>
            );
          })}
        </div>
      )}

      {readiness.warnings.length > 0 && (
        <div className="mt-2 space-y-1" data-testid="readiness-warnings">
          {readiness.warnings.slice(0, 4).map((w, i) => (
            <p key={i} className="flex items-start gap-1.5 text-[11px] font-semibold text-amber-800">
              <AlertTriangle size={11} className="mt-0.5 shrink-0" /> {w}
            </p>
          ))}
        </div>
      )}
      {readiness.level === "Not Ready" || readiness.level === "Underprepared" ? (
        <p className="mt-2 text-[10px] italic text-muted-foreground">
          Missing core basics will genuinely hurt your Day-120 score (−{readiness.penalty} pts). Focus on the core skills above.
        </p>
      ) : null}
    </div>
  );
};
